import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import Chevron from "@RHCommerceDev/icon-dropdown-v2";
import { cn } from "@RHCommerceDev/utils/cn";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    icon?: React.ReactNode;
  }
>(({ className, children, icon, ...props }, ref) => {
  const Icon = icon || (
    <Chevron
      className="mr-1 h-6 w-6 opacity-50 transition-transform group-data-[state=open]:rotate-180"
      aria-hidden="true"
    />
  );

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "group flex h-12 w-full items-center justify-between whitespace-nowrap border border-solid border-gray-200 bg-rh-buff px-[14px] py-2 text-black disabled:opacity-50",
        className
      )}
      aria-haspopup="listbox"
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>{Icon}</SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> & {
    iconProps?: React.ComponentPropsWithoutRef<"svg">;
  }
>(({ className, iconProps, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    tabIndex={-1}
    aria-label="Scroll up"
    {...props}
  >
    <Chevron
      {...iconProps}
      className={cn(
        "mr-1 h-6 w-6 rotate-180 opacity-50",
        iconProps?.className || ""
      )}
      aria-hidden="true"
    />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> & {
    iconProps?: React.ComponentPropsWithoutRef<"svg">;
  }
>(({ className, iconProps, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    tabIndex={-1}
    aria-label="Scroll down"
    {...props}
  >
    <Chevron
      {...iconProps}
      className={cn("mr-1 h-6 w-6 opacity-50", iconProps?.className || "")}
      aria-hidden="true"
    />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-[1300] max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md shadow-[0_4px_4px_0px_rgba(0,0,0,0.15)]",
        className
      )}
      role="listbox"
      aria-orientation="vertical"
      {...props}
    >
      <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  HTMLLabelElement,
  React.HTMLAttributes<HTMLLabelElement> & {
    className?: string;
    filled?: boolean;
    focused?: boolean;
  }
>(({ className, filled, ...props }, ref) => {
  return (
    <label
      ref={ref}
      className={cn(
        "pointer-events-none absolute z-10 bg-white text-[1rem] text-gray-400 transition-all duration-200",
        filled
          ? "left-[9px] top-[-8px] scale-[0.875]"
          : "left-[16px] top-[14px]",
        className
      )}
      id={props.id || "select-label"}
      {...props}
    />
  );
});
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "data-[disabled] :pointer-events-none relative flex w-full cursor-pointer select-none items-center rounded-sm px-4 py-2 hover:bg-gray-100 focus:outline-none data-[highlighted]:bg-gray-100 data-[disabled]:opacity-50",
      className
    )}
    role="option"
    tabIndex={-1}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("bg-muted -mx-1 my-1 h-px", className)}
    role="separator"
    aria-orientation="horizontal"
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton
};
